import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, List, ListItem, ListItemText, Typography } from '@mui/material';

import { OrderSettingsApi } from '@/api/domains/order-settings.api';
import { OrderSettingsQueryKeys } from '@/api/domains/order-settings.query-keys';
import type { OrderSettingsPayload } from '@/api/domains/order-settings.types';
import { ReconciliationApi } from '@/api/domains/reconciliation.api';
import { RoundedButton } from '@/components/buttons';
import {
  DialogActionsContainer,
  DialogContentContainer,
  DialogHeader,
  useGlobalErrorStore,
} from '@/components/dialogs';
import type { FormSchema } from '@/components/FieldMatching';
import { FieldMatchingForm as FieldMatchingFormBase } from '@/components/FieldMatching';
import {
  RECONCILIATION_ALL_FIELDS,
  RECONCILIATION_COLUMN_MAKER,
  RECONCILIATION_COLUMN_NAME,
  RECONCILIATION_COLUMN_NAME_AND_MAKER,
  RECONCILIATION_REQUIRED_FIELDS,
} from '@/pages/orders/reconciliation/reconciliation.constants';
import { OrdersPaths } from '@/shared/lib/react-router';

import {
  resetReconciliationFileStore,
  useReconciliationDistributorId,
  useReconciliationDocumentType,
  useReconciliationFile,
  useReconciliationFileStoreActions,
  useReconciliationOrderGuid,
} from '../reconciliation-file.store';
import { ReconciliationFieldMatchingDataGrid } from './grid';

type RequiredFieldsWarningDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

const RequiredFieldsWarningDialog = (props: RequiredFieldsWarningDialogProps) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation('orders-page');

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogHeader
        variant="warning"
        title={t('reconciliation.field-matching.required-fields.dialog-title')}
        onClose={onClose}
      />
      <DialogContentContainer>
        <Typography variant="Body/medium" color="neutral.700">
          {t('reconciliation.field-matching.required-fields.title')}
        </Typography>
        <List sx={{ listStyleType: 'disc', pl: 2, pt: 0.5 }}>
          <ListItem sx={{ display: 'list-item', p: 0, mb: 0.5, color: 'neutral.700' }}>
            <ListItemText
              primary={t('reconciliation.field-matching.columns.name')}
              primaryTypographyProps={{ variant: 'Body/medium', color: 'neutral.700' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', p: 0, mb: 0.5, color: 'neutral.700' }}>
            <ListItemText
              primary={t('reconciliation.field-matching.columns.maker')}
              primaryTypographyProps={{ variant: 'Body/medium', color: 'neutral.700' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', p: 0, mb: 0.5, color: 'neutral.700' }}>
            <ListItemText
              primary={t('reconciliation.field-matching.columns.count')}
              primaryTypographyProps={{ variant: 'Body/medium', color: 'neutral.700' }}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', p: 0, mb: 0, color: 'neutral.700' }}>
            <ListItemText
              primary={t('reconciliation.field-matching.columns.price_with_vat')}
              primaryTypographyProps={{ variant: 'Body/medium', color: 'neutral.700' }}
            />
          </ListItem>
        </List>
      </DialogContentContainer>
      <DialogActionsContainer>
        <RoundedButton variant="contained" onClick={onClose}>
          {t('reconciliation.field-matching.required-fields.close')}
        </RoundedButton>
      </DialogActionsContainer>
    </Dialog>
  );
};

const areRequiredColumnsSelected = (columns: Record<string, string>) => {
  const selectedColumns = Object.values(columns).filter(Boolean);
  const hasNameAndMaker = selectedColumns.includes(RECONCILIATION_COLUMN_NAME_AND_MAKER);

  const requiredFields = hasNameAndMaker
    ? RECONCILIATION_REQUIRED_FIELDS.filter(
        field => field !== RECONCILIATION_COLUMN_NAME && field !== RECONCILIATION_COLUMN_MAKER,
      )
    : RECONCILIATION_REQUIRED_FIELDS;

  return requiredFields.every(field => selectedColumns.includes(field));
};

// ----------------------------------------------------------------------

export const ReconciliationFieldMatchingForm = () => {
  const navigate = useNavigate();
  const documentType = useReconciliationDocumentType();
  const distributorId = useReconciliationDistributorId();
  const orderGuid = useReconciliationOrderGuid();
  const reconciliationFile = useReconciliationFile();
  const { setReconciliationId } = useReconciliationFileStoreActions();
  const { setGlobalError } = useGlobalErrorStore();

  const hasDistributorId = distributorId !== null && distributorId > 0;

  const { data: savedSettings } = useQuery({
    queryKey: OrderSettingsQueryKeys.settings(documentType, distributorId ?? 0),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => OrderSettingsApi.getSettings(documentType, distributorId!),
    enabled: hasDistributorId,
  });

  const { mutateAsync: saveSettings, isLoading: isSavingSettings } = useMutation({
    mutationFn: (params: { distributorId: number; payload: OrderSettingsPayload }) =>
      OrderSettingsApi.saveSettings(documentType, params.distributorId, params.payload),
  });

  const { mutateAsync: importFile, isLoading: isImporting } = useMutation({
    mutationFn: (file: File) => ReconciliationApi.importFile(orderGuid, documentType, file),
  });

  const isLoading = isSavingSettings || isImporting;

  const handleSubmit = async (formData: FormSchema) => {
    if (distributorId === null || !reconciliationFile) return;

    const { startRow, columns } = formData;

    const assignedColumns = Object.entries(columns)
      .filter(([, value]) => Boolean(value))
      .map(([columnKey, fieldKey]) => ({
        key: fieldKey,
        number: Number(columnKey.replace('column-', '')),
      }));

    const assignedKeys = new Set(assignedColumns.map(col => col.key));
    const unassignedColumns = RECONCILIATION_ALL_FIELDS.filter(field => !assignedKeys.has(field)).map(field => ({
      key: field,
      number: 0,
    }));

    const columnMappings = [...assignedColumns, ...unassignedColumns];

    try {
      await saveSettings({
        distributorId,
        payload: {
          start_row: Number(startRow),
          columns: columnMappings,
        },
      });

      const result = await importFile(reconciliationFile);

      if (result?.reconciliation_id) {
        setReconciliationId(result.reconciliation_id);
      }

      navigate(OrdersPaths.RECONCILIATION_BINDING);
    } catch (error) {
      setGlobalError(error);
    }
  };

  const handleCancel = () => {
    resetReconciliationFileStore();
    navigate(OrdersPaths.ROOT);
  };

  return (
    <FieldMatchingFormBase
      isLoading={isLoading}
      fieldMatchingSettings={savedSettings}
      requiredColumnsDialogMessage={null}
      validateColumns={areRequiredColumnsSelected}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      renderWarningDialog={({ open, onClose }) => <RequiredFieldsWarningDialog isOpen={open} onClose={onClose} />}
    >
      <ReconciliationFieldMatchingDataGrid fieldMatchingSettings={savedSettings} />
    </FieldMatchingFormBase>
  );
};
